import React from "react";

import { NewDashboardDialog } from "#app/components/dashboards/new-dashboard-dialog";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { CreateTeamDialog } from "#app/components/teams/create-team-dialog";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";
import { WorkflowShareDialog } from "#app/components/workflows/workflow-share-dialog";
import { useAppData } from "#app/lib/data";
import { createDialogEmitter } from "#app/lib/dialog-emitter";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";

/**
 * The dialogs the navigation pane opens — create workflow, new dashboard,
 * create team, invite members, share workflow — mounted ONCE at the workspace
 * level, never inside the module menu that asks for them.
 *
 * On a phone the module menu renders inside the compact nav drawer, and every
 * one of these rows closes that drawer before it opens its dialog. A dialog
 * the menu hosted itself unmounted with the drawer a beat after it opened, so
 * the press read as "the dialog and the sidebar both closed". Hosting them
 * here keeps the dialog alive while the drawer slides away. The caller still
 * owns the permission gate: a row without its grant never calls open.
 */

const createWorkflowDialog = createDialogEmitter("create_workflow");
const newDashboardDialog = createDialogEmitter("new_dashboard");
const createTeamDialog = createDialogEmitter("create_team");
const inviteMembersDialog = createDialogEmitter("invite_members");
const workflowShareDialog = createDialogEmitter<string>("workflow_share");

type NavDialogActions = {
  openCreateWorkflow: () => void;
  openNewDashboard: () => void;
  openCreateTeam: () => void;
  openInviteMembers: () => void;
  /** Open the share dialog for one workflow. */
  openWorkflowShare: (workflowId: string) => void;
};

/** Stable open callbacks. Works from inside the drawer and its portals. */
export function useNavDialogActions(): NavDialogActions {
  return React.useMemo<NavDialogActions>(
    () => ({
      openCreateWorkflow: () => createWorkflowDialog.open(),
      openNewDashboard: () => newDashboardDialog.open(),
      openCreateTeam: () => createTeamDialog.open(),
      openInviteMembers: () => inviteMembersDialog.open(),
      openWorkflowShare: (workflowId) => workflowShareDialog.open(workflowId),
    }),
    [],
  );
}

export function NavDialogsMount() {
  const { push: navigate } = useAppNavigate();
  const { refetch: refetchAppData } = useAppData();
  const createWorkflow = createWorkflowDialog.useDialogState();
  const newDashboard = newDashboardDialog.useDialogState();
  const createTeam = createTeamDialog.useDialogState();
  const inviteMembers = inviteMembersDialog.useDialogState();
  const workflowShare = workflowShareDialog.useDialogState();

  return (
    <>
      <CreateWorkflowDialog
        open={createWorkflow.open}
        onOpenChange={createWorkflow.setOpen}
        onCreated={async (workflowId) => {
          // App data feeds the module menu's workflow list; then land on the
          // new workflow's own page — only the settings list lands in
          // settings; every other entry point stays in the app.
          await refetchAppData();
          navigate(`/workflow/${workflowId}`);
        }}
      />
      <NewDashboardDialog
        via="nav_menu"
        open={newDashboard.open}
        onOpenChange={newDashboard.setOpen}
      />
      <CreateTeamDialog
        open={createTeam.open}
        onOpenChange={createTeam.setOpen}
        onCreated={async (teamId) => {
          // Same landing as a workflow: refresh the menu's team list, then
          // the new team's own page.
          await refetchAppData();
          navigate(`/team/${teamId}`);
        }}
      />
      <InviteMembersDialog
        open={inviteMembers.open}
        onOpenChange={inviteMembers.setOpen}
      />
      <WorkflowShareDialog
        workflowId={workflowShare.open ? workflowShare.payload : null}
        onOpenChange={workflowShare.setOpen}
      />
    </>
  );
}
